import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import icfCover from "@/assets/icf-cover.png";
import { Seo } from "@/components/Seo";

const PDF_URL =
  "https://jqswmyqzmjdstetjqzvv.supabase.co/storage/v1/object/public/edlr-icf2025/EDLR_ICF2025.pdf";

const IndiceCitoyennete = () => {
  return (
    <div className="min-h-screen flex flex-col bg-[#FFFD4C]">
      <Seo title="Indice de la citoyenneté française | EDLR" description="L'indice de la citoyenneté française : baromètre de l'attachement des Français aux valeurs et principes républicains." path="/indice-citoyennete" />
      <Header />

      <main className="flex-1 pt-28 pb-16 bg-gradient-to-b from-[#FFFD4C] via-[#FFFD4C] to-[#F5E84A]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1E3A8A]">
              Indice de la Citoyenneté Française (ICF)
            </h1>
          </div>
          {/* Top section: text + cover */}
          <section className="grid md:grid-cols-2 gap-8 items-stretch max-w-6xl mx-auto">
            {/* Left: intro text in soft card */}
            <div className="h-full bg-gray-200/80 backdrop-blur-sm rounded-2xl p-4 md:p-5 shadow-sm text-justify text-[#1E3A8A] [&_*]:text-[#1E3A8A] leading-snug text-[14px] space-y-2">
              <p>
                Il se trouve des situations, des moments pour lesquels on ne reste pas spectateur. **
              </p>
              <p>
                **On parle beaucoup de la République. On la défend, on l'invoque, on la célèbre, parfois on la pleure. Mais qui la mesure ? Qui s'attache, concrètement, à savoir si son état de santé est bon ? Si son socle, notre engagement citoyen envers ses valeurs, ses principes, ses lois, sont solides et partagés ?
              </p>
              <p>
                Pour aborder ces sujets factuellement, sans ressenti, nous avons créé l'Indice de Citoyenneté Française (ICF), miroir de notre relation aux principes républicains, à notre modèle de société qui aspire à la Liberté, à la dignité des individus et à la cohésion sociale dans un état de droit. Pas un sondage. Pas une pétition. Un indice, chiffré, sourcé, public, suivi année après année.
              </p>
              <p>
                L'ICF repose sur 10 indicateurs clés (cf tableau de synthèse), couvrant aussi bien les droits que les devoirs caractéristiques des valeurs, principes et lois contenus dans La Charte des Droits et devoirs du citoyen français. Ces indicateurs sont aussi choisis pour être suffisamment représentatifs de notre société et représentent équitablement les valeurs de Liberté, Egalité, Fraternité, Laïcité. La mesure de ces indicateurs en données brutes s'appuie uniquement sur des sources officielles indiscutables et vérifiables (INSEE, Ministères, organismes publics reconnus) en étant comparables sur 10 ans.
              </p>
              <p>C'est un outil de diagnostic, pas de jugement.</p>
            </div>

            {/* Right: cover with the printed CTA acting as the link */}
            <div className="mx-auto w-full max-w-md">
              <a
                href={PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Accéder au rapport complet sur l'ICF 2025 Enfants de la République"
                className="block rounded-2xl overflow-hidden border border-black/40 shadow-lg hover:shadow-xl transition-shadow"
              >
                <img
                  src={icfCover}
                  alt="Couverture du rapport ICF 2025 — L'Indice de Citoyenneté Française, Juillet 2025"
                  className="w-full h-auto block"
                />
              </a>
            </div>
          </section>

          {/* Bottom section: rest of the article */}
          <section className="max-w-6xl mx-auto mt-10">
            <div className="bg-gray-200/80 backdrop-blur-sm rounded-2xl p-4 md:p-5 shadow-sm text-justify text-[#1E3A8A] [&_*]:text-[#1E3A8A] leading-snug text-[14px] space-y-2">
              <p>
                En 2024, le score global est de 48 sur 100<sup>**</sup> indiquant d'emblée la tiédeur de notre engagement citoyen mais, surtout, ce que montre l'ICF sur 10 ans est clair : la République avance à deux vitesses. D'un côté, les droits progressent, passant de 36 sur 100 en 2015 à 62 sur 100 en 2024 : on vote librement, la presse regagne en indépendance, l'égalité salariale femmes/hommes s'améliore et l'accès aux aides sociales demeure solide. De l'autre côté, les devoirs s'affaiblissent fortement, passant sur la même période de 64 sur 100 à 34 sur 100 ! Les violences interpersonnelles augmentent. Les atteintes à la laïcité en milieu scolaire se multiplient. Le devoir civique recule.
              </p>
              <p className="font-semibold">
                C'est là l'alerte majeure. Une citoyenneté déséquilibrée : des droits obtenus mais des devoirs délaissés.
              </p>
              <p>
                L'ICF n'est pas là pour pointer des fautes. Il est là pour éclairer, alerter, susciter l'action. Ce que nous mesurons, nous pouvons l'améliorer.
              </p>
              <p>
                Cette mesure de notre citoyenneté à partir d'un texte sobre et puissant signé par tout nouveau citoyen lors d'une naturalisation doit permettre d'enclencher un nouvel élan de partage de ce pacte par tous les français permettant de construire ensemble un avenir où nos droits et nos devoirs bénéficient de la même énergie, un avenir respectueux des principes qui nous unissent !
              </p>
              <p>
                Aussi, nous proposons, dans un premier temps, que la Charte soit redécouverte, revalorisée, véritablement enseignée. Qu'elle entre dans les écoles, les administrations, la vie publique car une République qui ne rappelle plus à ses membres ce qu'elle est, est une République qui s'efface ainsi que malheureusement ces chiffres montrent. Dans un deuxième temps, nous devons, ensemble, ouvrir le débat sur l'extension de son champ et de ses modalités d'application tout en protégeant celle-ci de ses contrevenants.
              </p>
              <p>
                Enfants de la République a choisi de relever ce défi, redonner vie à notre citoyenneté afin de la transmettre intacte dans son esprit à nos propres enfants. Si la citoyenneté est une construction commune, alors chacun a sa part de responsabilité dans sa vitalité.
              </p>
              <p className="font-semibold">
                Nous sommes une force de proposition pour un projet collectif contre la fragmentation de notre République car elle n'est pas un acquis : elle est une exigence. Une exigence de vérité, de courage et de transmission que, pour notre part, nous vivons comme une chance.
              </p>
              <p className="text-xs italic pt-4">
                * Association de loi 1901 créée en 2025 pour transmettre, renforcer et protéger la Charte des droits et des devoirs du citoyen français (art 21-24 du code civil).
              </p>
              <p className="text-xs italic">
                ** Le rapport complet ICF Enfants de la République est disponible sur ledlr.org
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IndiceCitoyennete;
